import {
    createContext,
    memo,
    useContext,
    type ComponentProps,
    type ReactNode,
} from "react";
import Markdown, { type ExtraProps } from "react-markdown";
import remarkGfm from "remark-gfm";

// Text written by a model (the assistant's answer, the result of a mail analysis), rendered through
// an allowlist of simple formatting. A mail the model has read can make it write an address that
// carries the content of other mails: nothing it writes may load or open anything. A link keeps its
// text and goes nowhere, an image is dropped, raw HTML is ignored. The check runs on the elements
// about to be displayed, whatever the Markdown syntax that produced them (inline, reference, bare URL).
// What is clickable comes from the app: a ref the answer cites ([m3], [e2], [c1]), given to
// `renderRef`, which renders the element the app holds for it.
const ALLOWED_ELEMENTS = [
    "p",
    "br",
    "hr",
    "strong",
    "em",
    "del",
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    "ul",
    "ol",
    "li",
    // The disabled box of a task list item (« - [ ] … »).
    "input",
    "blockquote",
    "code",
    "pre",
    "table",
    "thead",
    "tbody",
    "tr",
    "th",
    "td",
    // A ref cited by the answer (raw HTML being ignored, only `remarkAnswerRefs` makes one).
    "span",
];

const REF = /\[([mec]\d{1,4})\]/g;

type MdNode = {
    type: string;
    value?: string;
    children?: MdNode[];
    data?: Record<string, unknown>;
};

/** Neighbouring text nodes joined, then each ref cut out of the text as a node of its own. */
const splitRefs = (node: MdNode): void => {
    if (!node.children) return;
    const joined: MdNode[] = [];
    for (const child of node.children) {
        const previous = joined[joined.length - 1];
        if (child.type === "text" && previous?.type === "text") {
            previous.value = (previous.value ?? "") + (child.value ?? "");
        } else {
            joined.push(child.type === "text" ? { ...child } : child);
        }
    }
    node.children = joined.flatMap((child) => {
        if (child.type !== "text") {
            splitRefs(child);
            return [child];
        }
        const text = child.value ?? "";
        const parts: MdNode[] = [];
        let last = 0;
        for (const match of text.matchAll(REF)) {
            const index = match.index ?? 0;
            if (index > last) {
                parts.push({ type: "text", value: text.slice(last, index) });
            }
            parts.push({
                type: "answerRef",
                data: { hName: "span", hProperties: { dataRef: match[1] } },
                children: [{ type: "text", value: match[0] }],
            });
            last = index + match[0].length;
        }
        if (last === 0) return [child];
        if (last < text.length) {
            parts.push({ type: "text", value: text.slice(last) });
        }
        return parts;
    });
};

const remarkAnswerRefs = () => (tree: MdNode) => splitRefs(tree);

type RenderRef = (ref: string) => ReactNode | undefined;

// Given through a context so that the component below keeps its identity from one render to the
// next: a new one each time would remount every element (a chip loses focus, flickers).
const RenderRefContext = createContext<RenderRef | undefined>(undefined);

/** A ref cited by the answer: the element the app holds for it, or the ref as text. */
const AnswerRef = ({ node, children }: ComponentProps<"span"> & ExtraProps) => {
    const renderRef = useContext(RenderRefContext);
    const ref = node?.properties?.dataRef;
    const element = typeof ref === "string" ? renderRef?.(ref) : undefined;
    return element ?? <>{children}</>;
};

const COMPONENTS = { span: AnswerRef };
const REMARK_PLUGINS = [remarkGfm, remarkAnswerRefs];

interface ModelMarkdownProps {
    children: string;
    /** The element a ref stands for; absent, or `undefined` for this ref: the ref stays text. */
    renderRef?: RenderRef;
}

const ModelMarkdown = ({ children, renderRef }: ModelMarkdownProps) => (
    <RenderRefContext.Provider value={renderRef}>
        <Markdown
            remarkPlugins={REMARK_PLUGINS}
            allowedElements={ALLOWED_ELEMENTS}
            unwrapDisallowed
            skipHtml
            components={COMPONENTS}
        >
            {children}
        </Markdown>
    </RenderRefContext.Provider>
);

// The Markdown of an answer is parsed again only when its text or its refs change (audit 15/10, P4-03).
export default memo(ModelMarkdown);
